// React
import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

// Hooks
import { useSettings } from 'shared/utils/useSettings'
import { useOrm } from 'shared/utils/useOrm'
import { usePrevious } from 'shared/utils/usePrevious'
import { useSelector } from 'react-redux'

// Actions
import ClientActions from 'store/v2/redux/actions/client'
import ItemActions from 'store/v2/redux/actions/item'
import CartActions from 'store/v2/redux/actions/cart'

// Shared
import moment from 'shared/lib/moment'
import { fetchCountriesAndProvinces } from 'back_office/modules/addresses/components/fetchCountriesAndProvinces'

/**
 * @typedef {Object} DeliveryAddress
 *
 * @property {string} address1
 * @property {string} address2
 * @property {string} city
 * @property {string} country
 * @property {string} country_id
 * @property {string} first_name
 * @property {string} last_name
 * @property {string} region
 * @property {string} province_id
 * @property {string} zipcode
 */

/**
 * @typedef {Object} InitialValues
 *
 * @property {import('moment').Moment | null} starts_at - Start date of the cart
 * @property {import('moment').Moment | null} stops_at - Stop date of the cart
 * @property {string | null} start_location_id - ID of the pickup location
 * @property {string | null} stop_location_id - ID of the return location
 * @property {DeliveryAddress} delivery_address - Delivery address of the cart
 * @property {Object} data - Additional data of the cart
 */

/**
 * @typedef {Object} Cart
 *
 * @property {string | null} starts_at
 * @property {string | null} stops_at
 * @property {string | null} start_location_id
 * @property {string | null} stop_location_id
 */

/**
 * @typedef {Object} CartPickerContextValue
 *
 * @property {Cart} cart - The current cart
 * @property {InitialValues} initialValues - Initial values for the cart form
 * @property {(payload: Object) => Promise<void>} save - Async function to save cart changes
 * @property {(valid: boolean) => void} validate - Function to validate cart state
 * @property {boolean} isValid - Whether the current cart state is valid
 * @property {boolean} isLoading - Whether the cart is currently loading
 * @property {boolean} isInitialized - Whether the cart has been initialized
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setLoading - Function to manually set loading state
 */

/**
 * React Context for the CartPicker component.
 * Provides cart-related state and operations to child components.
 * @type {React.Context<CartPickerContextValue>}
 */
export const CartPickerContext = createContext({
  cart: {
    starts_at: null,
    stops_at: null,
    start_location_id: null,
    stop_location_id: null
  },
  initialValues: {
    starts_at: null,
    stops_at: null,
    start_location_id: null,
    stop_location_id: null,
    delivery_address: {
      address1: '',
      address2: '',
      city: '',
      country: '',
      country_id: '',
      first_name: '',
      last_name: '',
      region: '',
      province_id: '',
      zipcode: ''
    },
    data: {}
  },
  onSave: async () => {},
  onValidate: () => {},
  isValid: false,
  isLoading: false,
  isInitialized: false,
  setLoading: () => {}
})

/**
 * Provider component for CartPickerContext.
 * Manages cart state and provides cart-related operations to its children.
 *
 * @param {Object} props
 * @param {React.ReactNode | ((context: CartPickerContextValue) => React.ReactNode)} props.children - Child components that will have access to the cart context
 *
 * @example
 * return (
 *   <CartPickerProvider>
 *     <CartForm />
 *   </CartPickerProvider>
 * )
 */
export const CartPickerProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const id = useSelector((state) => state.client.cartId)
  const supportedFulfillmentTypes = useSelector((state) => state.client.supportedFulfillmentTypes)

  const cart = useOrm('carts', (carts) => {
    return carts.getById(id || window.booqableOptions.cart?.id)
  })
  const locations = useOrm('locations', (collection) => {
    return collection.getAll({ archived_at: [null, undefined] })
  })
  const pickupLocations = useOrm('locations', (locations) => {
    return locations
      .getAll({ archived_at: [null, undefined], location_type: 'rental' })
      .filter((location) => location.fulfillment_capabilities.includes('pickup'))
  })
  const deliveryLocations = useOrm('locations', (locations) => {
    return locations
      .getAll({ archived_at: [null, undefined] })
      .filter((location) => location.fulfillment_capabilities.includes('delivery'))
  })

  const behavior = useSettings('store.behaviors.location_picker', { default: 'start_stop' })
  const defaultStartLocationId = useSettings('defaults.shop_start_location_id')
  const defaultStopLocationId = useSettings('defaults.shop_stop_location_id')
  const companyCountry = useSettings('main_address_attributes.country')
  const companyCountryId = useSettings('main_address_attributes.country_id')
  const deliveryCountries = useSettings('store.delivery_country_names', { default: [] })
  const requireDeliveryAddress = useSettings('store.require_delivery_address_when_selecting_rental_period')

  const countries = useOrm('countries', (collection) => {
    return collection.getAll({ ...(deliveryCountries.length ? { name: deliveryCountries } : {}) })
  })
  const provinces = useOrm('provinces', (provinces) => {
    return provinces.getAll()
  })

  const country = useMemo(() => {
    if (countries.size() === 1) {
      return countries.first()
    }

    return {
      id: companyCountryId,
      name: companyCountry
    }
  }, [countries, companyCountry, companyCountryId, deliveryCountries])

  const initialValues = useMemo(() => {
    const deliveryAddress = {}

    if (requireDeliveryAddress) {
      Object.assign(deliveryAddress, {
        address1: '',
        address2: '',
        city: '',
        country: country?.name || '',
        country_id: country?.id || '',
        first_name: '',
        last_name: '',
        region: '',
        province_id: '',
        zipcode: ''
      })
    }

    const values = {
      starts_at: null,
      stops_at: null,
      start_location_id: null,
      stop_location_id: null,
      delivery_address: deliveryAddress,
      data: {}
    }

    if (cart) {
      Object.assign(values, {
        starts_at: cart.starts_at ? moment(cart.starts_at) : null,
        stops_at: cart.stops_at ? moment(cart.stops_at) : null,
        start_location_id: cart.start_location_id || null,
        stop_location_id: cart.stop_location_id || null,
        data: {
          fulfillment_type: cart.data?.fulfillment_type
        },
        delivery_address: requireDeliveryAddress ? cart.data?.delivery_address || values.delivery_address : {}
      })
    }

    if (!values.data.fulfillment_type) {
      if (pickupLocations.size()) {
        values.data.fulfillment_type = 'pickup'
      } else if (deliveryLocations.size()) {
        values.data.fulfillment_type = 'delivery'
      }

      if (!values.data.fulfillment_type) {
        values.data.fulfillment_type = 'pickup'
      }
    }

    return values
  }, [cart, companyCountry, companyCountryId, pickupLocations.size(), deliveryLocations.size(), country, requireDeliveryAddress])

  const isPending = useOrm('pendingRequests', (requests) => {
    return requests.includes('CARTS_FETCH')
  })
  const isPrevPending = usePrevious(isPending)

  useEffect(() => {
    if (!isInitialized && !isPending && isPrevPending && !!locations.size()) {
      setIsInitialized(true)
    }
  }, [isInitialized, isPending, isPrevPending, locations])

  useEffect(() => {
    if (!supportedFulfillmentTypes.includes('delivery')) return

    if (!countries.size() || !provinces.size()) {
      fetchCountriesAndProvinces({
        filter: deliveryCountries.length ? { name: deliveryCountries } : undefined
      })
    }
  }, [deliveryCountries, supportedFulfillmentTypes, countries.size(), provinces.size()])

  const handleValidate = useCallback((valid) => {
    setIsValid(valid)
  }, [])

  const handleSave = useCallback(
    async (payload) => {
      const values = { ...payload }

      switch (behavior) {
        case 'start':
          values.stop_location_id = values.start_location_id
          break
        case 'auto':
          values.start_location_id = defaultStartLocationId
          values.stop_location_id = defaultStopLocationId
          break
        default:
          break
      }

      if (values.data.fulfillment_type === 'delivery') {
        values.data.delivery_address = values.delivery_address

        delete values.delivery_address
      }

      setIsLoading(true)

      try {
        const response = await CartActions.update({
          ...values,
          starts_at: moment(values.starts_at),
          stops_at: moment(values.stops_at)
        })

        await ItemActions.fetchAllOnPage(response.payload.response.cart.id)

        ClientActions.evaluateCartOverdue()

        setIsLoading(false)

        return response
      } catch (error) {
        console.error('Error saving cart', error)

        throw error
      } finally {
        setIsLoading(false)
      }
    },
    [behavior, defaultStartLocationId, defaultStopLocationId]
  )

  const value = {
    cart,
    initialValues,
    isLoading,
    isValid,
    isInitialized,
    save: handleSave,
    validate: handleValidate,
    setLoading: setIsLoading
  }

  return (
    <CartPickerContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </CartPickerContext.Provider>
  )
}

CartPickerProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
}
